/* eslint-disable no-continue */
import { createSelector } from '@reduxjs/toolkit';
import { memoize } from 'lodash-es';
import { hasPermission, Permission, Role } from 'zl-shared/dist/auth';

import rules from '@/components/Authorize/rules';
import { defaultSorterTypes, HabitatSortType, sortHabitats } from '@/helpers/habitatsSorter';
import { HabitatListItem } from '@/queries/habitats/types';

import { AppState } from '../helper';
import { selectIsFreeUser } from './users';

export const selectAllFreemiumHabitatsMap = createSelector(
  (state: AppState) => state.user.logged,
  (state: AppState) => state.user.role,
  (state: AppState) => state.user.subscription.active,
  (state: AppState) => state.allHabitats,
  (logged, role, active, allHabitats) => {
    const map: Record<string, boolean> = {};
    allHabitats.forEach((habitat) => {
      if (!logged) {
        map[habitat._id] = true;
      } else if (!active && !hasPermission(Permission.Map.AllHabitats, null, role as Role, rules)) {
        map[habitat._id] = !habitat.isFreemium;
      } else {
        map[habitat._id] = false;
      }
    });
    return map;
  },
);

export const selectIsHabitatLocked = memoize((habitatId: string | undefined) =>
  habitatId
    ? createSelector(selectAllFreemiumHabitatsMap, (map: Record<string, boolean>) => map[habitatId] ?? false)
    : () => false,
);

export const selectAllPremiumHabitatsAsPaidUserMap = createSelector(
  (state: AppState) => state.user.logged,
  (state: AppState) => state.user.role,
  (state: AppState) => state.user.subscription.active,
  (state: AppState) => state.allHabitats,
  (logged, role, active, allHabitats) => {
    const map: Record<string, boolean> = {};
    const canAccessAll = active || hasPermission(Permission.Map.AllHabitats, null, role as Role, rules);
    allHabitats.forEach((habitat) => {
      if (!logged) {
        map[habitat._id] = false;
      } else {
        map[habitat._id] = canAccessAll && !habitat.isFreemium;
      }
    });
    return map;
  },
);

export const createSortedHabitatsSelector = (
  sorterTypes: readonly HabitatSortType<HabitatListItem>[] = defaultSorterTypes,
) =>
  createSelector(
    (state: AppState) => state.allHabitats,
    selectIsFreeUser,
    (state: AppState) => state.remoteConfig.lastMediaCaptureThresholdForFeaturedHabitatMinutes,
    selectAllFreemiumHabitatsMap,
    (allHabitats, isFreeUser, threshold, lockedMap) => {
      const sorted = sortHabitats(allHabitats, isFreeUser, sorterTypes, threshold);
      return sorted.map((habitat) => ({
        ...habitat,
        isLocked: lockedMap[habitat._id] ?? false,
      }));
    },
  );
